import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FaEdit, FaLock, FaLockOpen, FaPlus, FaSearch, FaTrash } from 'react-icons/fa';
import DataTable from '../DataTable/DataTable.jsx';
import Pagination from '../Pagination/Pagination.jsx';
import spacesApi from '../../services/spacesApi.js';
import usuariosApi from '../../services/usuariosApi.js';
import { colors } from '../../utils/colors.js';

const STATUS_LABELS = {
  reservado: 'Reservado',
  sistema: 'Sistema',
  liberado: 'Liberado',
};

const STATUS_TONES = {
  reservado: 'warning',
  sistema: 'info',
  liberado: 'success',
};

const emptyForm = { slug: '', motivo: '', destinatario: null };

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const SearchBox = styled.div`
  position: relative;
  min-width: 240px;
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.gray[400]};
  font-size: 13px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 34px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
`;

const FilterSelect = styled.select`
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${colors.gray[700]};
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  background: ${colors.gradients.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
`;

const Badge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${(props) => colors[props.$tone]?.light || colors.gray[100]};
  color: ${(props) => colors[props.$tone]?.dark || colors.gray[600]};
`;

const SubText = styled.div`
  font-size: 12px;
  color: ${colors.gray[500]};
`;

const Actions = styled.div`
  display: flex;
  gap: 2px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${(props) => (props.$danger ? colors.error.main : colors.gray[500])};
  font-size: 14px;
  padding: 6px;

  &:hover {
    color: ${(props) => (props.$danger ? colors.error.dark : colors.primary[600])};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: stretch;
  }
`;

const ModalCard = styled.form`
  background: ${colors.white};
  overflow-y: auto;
  width: 480px;
  max-width: 100%;
  height: 100vh;
  padding: 28px;

  @media (max-width: 768px) {
    width: 100vw;
  }
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  margin: 0 0 20px;
  color: ${colors.gray[900]};
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray[700]};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;

  &:disabled {
    background: ${colors.gray[50]};
    color: ${colors.gray[500]};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 90px;
  resize: vertical;
`;

const Hint = styled.p`
  font-size: 12px;
  color: ${colors.gray[500]};
  margin: -10px 0 16px;
`;

const UserResults = styled.ul`
  list-style: none;
  margin: -10px 0 16px;
  padding: 0;
  border: 1px solid ${colors.gray[200]};
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

const UserOption = styled.li`
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 1px solid ${colors.gray[100]};

  &:hover {
    background: ${colors.primary[50]};
  }
`;

const SelectedUser = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${colors.primary[50]};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${colors.primary[600]};
  font-size: 13px;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: ${colors.gray[100]};
  color: ${colors.gray[700]};
`;

const SaveButton = styled(Button)`
  background: ${colors.gradients.primary};
  color: ${colors.white};
`;

const ErrorBox = styled.div`
  background: ${colors.error.light};
  color: ${colors.error.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

function getApiErrorMessage(error, fallback) {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail[0]?.msg || fallback;
  return fallback;
}

function shortId(id) {
  return id ? `${id.slice(0, 8)}…` : '—';
}

function reservaToForm(reserva) {
  const destinatario = reserva.reservado_para_usuario_id
    ? { id: reserva.reservado_para_usuario_id, label: reserva.reservado_para_usuario_id }
    : null;
  return { slug: reserva.slug, motivo: reserva.motivo || '', destinatario };
}

function formToPayload(form) {
  return {
    motivo: form.motivo.trim() || null,
    reservado_para_usuario_id: form.destinatario?.id || null,
  };
}

function DestinatarioPicker({ value, onChange }) {
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState([]);

  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 3) {
      setResultados([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      usuariosApi
        .list({ search: termo, per_page: 10 })
        .then((data) => setResultados(data.items || []))
        .catch(() => setResultados([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [busca]);

  const selecionar = (usuario) => {
    onChange({ id: usuario.id, label: `${usuario.user_name} (${usuario.email})` });
    setBusca('');
    setResultados([]);
  };

  if (value) {
    return (
      <SelectedUser>
        <span>{value.label}</span>
        <LinkButton type="button" onClick={() => onChange(null)}>
          Remover
        </LinkButton>
      </SelectedUser>
    );
  }

  return (
    <>
      <Input
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Buscar usuário por nome ou e-mail"
      />
      {resultados.length > 0 && (
        <UserResults>
          {resultados.map((usuario) => (
            <UserOption key={usuario.id} onClick={() => selecionar(usuario)}>
              {usuario.user_name} <SubText as="span">{usuario.email}</SubText>
            </UserOption>
          ))}
        </UserResults>
      )}
      <Hint>Opcional. Se definido, só esse usuário poderá criar o space com este nome.</Hint>
    </>
  );
}

function ReservaFormModal({ reserva, onClose, onSaved }) {
  const isEditing = Boolean(reserva);
  const [form, setForm] = useState(() => (reserva ? reservaToForm(reserva) : emptyForm));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEditing) {
        await spacesApi.updateReserva(reserva.id, formToPayload(form));
      } else {
        await spacesApi.createReserva({ slug: form.slug, ...formToPayload(form) });
      }
      onSaved();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a reserva.'));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <ModalTitle>{isEditing ? 'Editar reserva' : 'Nova reserva de nome'}</ModalTitle>
        {error && <ErrorBox>{error}</ErrorBox>}

        <Label htmlFor="reserva-slug">Nome (slug)</Label>
        <Input
          id="reserva-slug"
          value={form.slug}
          onChange={(e) => setField('slug', e.target.value)}
          placeholder="ex.: nike"
          disabled={isEditing}
          required
        />

        <Label htmlFor="reserva-motivo">Motivo</Label>
        <TextArea
          id="reserva-motivo"
          value={form.motivo}
          onChange={(e) => setField('motivo', e.target.value)}
          placeholder="ex.: marca parceira, aguardando contato"
        />

        <Label>Reservado para</Label>
        <DestinatarioPicker value={form.destinatario} onChange={(value) => setField('destinatario', value)} />

        <ModalActions>
          <CancelButton type="button" onClick={onClose}>
            Cancelar
          </CancelButton>
          <SaveButton type="submit" disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </SaveButton>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>,
    document.body
  );
}

export default function SpaceReservaManager() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [statusFiltro, setStatusFiltro] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [modalReserva, setModalReserva] = useState(undefined);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [statusFiltro, debouncedSearch, perPage]);

  const loadReservas = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: perPage };
    if (statusFiltro) params.status = statusFiltro;
    if (debouncedSearch) params.search = debouncedSearch;
    spacesApi
      .listReservas(params)
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, perPage, statusFiltro, debouncedSearch]);

  useEffect(() => {
    loadReservas();
  }, [loadReservas]);

  const alterarStatus = async (reserva, status) => {
    try {
      await spacesApi.updateReserva(reserva.id, { status });
      loadReservas();
    } catch (err) {
      window.alert(getApiErrorMessage(err, 'Não foi possível alterar o status.'));
    }
  };

  const excluir = async (reserva) => {
    if (!window.confirm(`Excluir a reserva "${reserva.slug}"?`)) return;
    try {
      await spacesApi.deleteReserva(reserva.id);
      loadReservas();
    } catch (err) {
      window.alert(getApiErrorMessage(err, 'Não foi possível excluir a reserva.'));
    }
  };

  const handleSaved = () => {
    setModalReserva(undefined);
    loadReservas();
  };

  const columns = [
    { key: 'slug', label: 'Nome' },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <Badge $tone={STATUS_TONES[row.status]}>{STATUS_LABELS[row.status] || row.status}</Badge>,
    },
    {
      key: 'reservado_para_usuario_id',
      label: 'Reservado para',
      render: (row) => (
        <span title={row.reservado_para_usuario_id || ''}>{shortId(row.reservado_para_usuario_id)}</span>
      ),
    },
    {
      key: 'motivo',
      label: 'Motivo',
      render: (row) => (
        <>
          {row.motivo || '—'}
          {row.space_id && <SubText>Usado pelo space {shortId(row.space_id)}</SubText>}
        </>
      ),
    },
    {
      key: 'created_at',
      label: 'Criado em',
      render: (row) => new Date(row.created_at).toLocaleDateString('pt-BR'),
    },
    {
      key: 'acoes',
      label: '',
      render: (row) =>
        row.status === 'sistema' ? null : (
          <Actions>
            {row.status === 'reservado' ? (
              <IconButton title="Liberar nome" onClick={() => alterarStatus(row, 'liberado')}>
                <FaLockOpen />
              </IconButton>
            ) : (
              <IconButton title="Reservar novamente" onClick={() => alterarStatus(row, 'reservado')}>
                <FaLock />
              </IconButton>
            )}
            <IconButton title="Editar" onClick={() => setModalReserva(row)}>
              <FaEdit />
            </IconButton>
            <IconButton title="Excluir" $danger onClick={() => excluir(row)}>
              <FaTrash />
            </IconButton>
          </Actions>
        ),
    },
  ];

  return (
    <div>
      <FilterBar>
        <SearchBox>
          <SearchIcon />
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar nome" />
        </SearchBox>
        <FilterSelect value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)}>
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </FilterSelect>
        <AddButton type="button" onClick={() => setModalReserva(null)}>
          <FaPlus /> Nova reserva
        </AddButton>
      </FilterBar>

      <DataTable columns={columns} rows={items} loading={loading} emptyMessage="Nenhuma reserva encontrada." />
      <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage} onPerPageChange={setPerPage} />

      {modalReserva !== undefined && (
        <ReservaFormModal reserva={modalReserva} onClose={() => setModalReserva(undefined)} onSaved={handleSaved} />
      )}
    </div>
  );
}
